import axios from 'axios';
import { httpClient } from '@/api/httpClient';
import {
  mapApiResourceToFirstConsultation,
  mapApiResourceToIntakeFields,
  mapApiResourceToOutcomeOptions,
  mapFirstConsultationWritePayload,
  mapIntakeFieldDrafts,
  mapOutcomeOptionDrafts,
  type FirstConsultation,
  type FirstConsultationApiResource,
  type FirstConsultationWritePayload,
  type IntakeField,
  type IntakeFieldDraft,
  type IntakeFieldsApiResource,
  type OutcomeOption,
  type OutcomeOptionDraft,
  type OutcomeOptionsApiResource,
} from './firstConsultationsApi.types';

export async function fetchConsultationIntakeFields(): Promise<IntakeField[]> {
  const response = await httpClient.get<IntakeFieldsApiResource>(
    '/me/workspace/consultation-intake-fields'
  );
  return mapApiResourceToIntakeFields(response.data);
}

export async function replaceConsultationIntakeFields(
  fields: IntakeFieldDraft[]
): Promise<IntakeField[]> {
  const response = await httpClient.put<IntakeFieldsApiResource>(
    '/me/workspace/consultation-intake-fields',
    mapIntakeFieldDrafts(fields)
  );
  return mapApiResourceToIntakeFields(response.data);
}

export async function fetchConsultationOutcomeOptions(): Promise<OutcomeOption[]> {
  const response = await httpClient.get<OutcomeOptionsApiResource>(
    '/me/workspace/consultation-outcome-options'
  );
  return mapApiResourceToOutcomeOptions(response.data);
}

export async function replaceConsultationOutcomeOptions(
  options: OutcomeOptionDraft[]
): Promise<OutcomeOption[]> {
  const response = await httpClient.put<OutcomeOptionsApiResource>(
    '/me/workspace/consultation-outcome-options',
    mapOutcomeOptionDrafts(options)
  );
  return mapApiResourceToOutcomeOptions(response.data);
}

export async function fetchPatientFirstConsultation(
  patientId: string
): Promise<FirstConsultation | null> {
  try {
    const response = await httpClient.get<FirstConsultationApiResource>(
      '/patients/' + patientId + '/first-consultation'
    );
    return mapApiResourceToFirstConsultation(response.data);
  } catch (error) {
    if (
      axios.isAxiosError(error) &&
      error.response !== undefined &&
      error.response.status === 404
    ) {
      return null;
    }
    throw error;
  }
}

export async function createFirstConsultation(
  patientId: string,
  payload: FirstConsultationWritePayload
): Promise<FirstConsultation> {
  const response = await httpClient.post<FirstConsultationApiResource>(
    '/patients/' + patientId + '/first-consultation',
    mapFirstConsultationWritePayload(payload)
  );
  return mapApiResourceToFirstConsultation(response.data);
}

export async function updateFirstConsultation(
  consultationId: string,
  payload: FirstConsultationWritePayload
): Promise<FirstConsultation> {
  const response = await httpClient.patch<FirstConsultationApiResource>(
    '/first-consultations/' + consultationId,
    mapFirstConsultationWritePayload(payload)
  );
  return mapApiResourceToFirstConsultation(response.data);
}

export async function uploadFirstConsultationDocument(
  consultationId: string,
  file: File
): Promise<FirstConsultation> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await httpClient.post<FirstConsultationApiResource>(
    '/first-consultations/' + consultationId + '/documents',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return mapApiResourceToFirstConsultation(response.data);
}

export async function removeFirstConsultationDocument(
  consultationId: string,
  documentId: string
): Promise<FirstConsultation> {
  const response = await httpClient.delete<FirstConsultationApiResource>(
    '/first-consultations/' + consultationId + '/documents/' + documentId
  );
  return mapApiResourceToFirstConsultation(response.data);
}
